import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { getFunc, postFunc } from "../../../services/mainApiServices.tsx";
import { toastFunc } from "../../../util/toast.ts";

import type { Dispatch } from "react";

export interface Organization {
  id?: string;
  organizationName?: string;
  organizationNameHr?: string;
  organizationNameSrb?: string;
  organizationNameEng?: string;
  city?: string;
  cityHr?: string;
  citySrb?: string;
  cityEng?: string;
  countryName?: string;
  address?: string;
  postalCode?: string;
  phoneNumber?: string;
  contactEmail?: string;
  pictureId?: string;
  pictureUrl?: string;
  numberPeople?: number;
  areaCode?: { code: string };
  faxNumber?: string;
}
export interface Service {
  name?: string;
  nameHr?: string;
  nameSrb?: string;
  nameEng?: string;
  services?: {
    serviceCategoryId: string;
    name: string;
    nameHr: string;
    nameSrb: string;
    nameEng: string;
    requiredDocuments: string[];
    id: string;
    maxNumberOfPersons: number;
    note?: string;
    noteHr?: string;
    noteSrb?: string;
    noteEng?: string;
  }[];
  id?: string;
  multiplePeople?: boolean;
  numberPeople?: number;
}

export interface WorkTime {
  id?: string;
  startTime?: string;
  endTime?: string;
  workPlannerId?: string;
}
export interface Confirmation {
  numberOfPeople: number;
  date: string;
  startTime: string;
  endTime?: string;
  notes: string;
  organizationId: string;
  serviceCategoryId: string;
  serviceId: string;
  timeSlotId: string;
  titleTypeValue: {
    name: string;
    value: number;
  };
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  street: string;
  city: string;
  countryId?: string;
}
export interface Status {
  code?: string;
  translatedStatus?: string;
  status?: {
    name: string;
    value: number;
  };
  categoryName?: string;
  serviceName?: string;
  date?: string;
  startTime?: string;
  numberOfPeople?: number;
  requiredDocuments?: string[];
  organizationName?: string;
  organizationNameHr?: string;
  organizationNameSrb?: string;
  organizationNameEng?: string;
  userEmail?: string;
  timeZone?: string;
  contactNumber?: string;
  contactEmail?: string;
  organizationAddress?: string;
}
export interface NoneWorkingDay {
  date: string;
  nonWorkingDayType: {
    name: string;
    value: number;
  };
  startTime: string;
  endTime: string;
  day: string;
}
export interface Validation {
  firstName?: string;
  lastName?: string;
  city?: string;
  street?: string;
  postalCode?: string;
  email?: string;
  phoneNumber?: string;
}

type Country = {
  countryName: string;
  countryNameEng: string;
  countryNameHr: string;
  countryNameSrb: string;
  id: string;
};
export interface ConfirmationData {
  numberOfPeople: number;
  date: string;
  startTime: string;
  endTime?: string;
  notes: string;
  organizationId: string;
  serviceCategoryId: string;
  serviceId: string;
  timeSlotId: string;
  titleTypeValue: {
    name: string;
    value: string;
  };
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  street: string;
  city: string;
  countryId: Country | undefined;
}

interface AppointmentState {
  organizations: Organization[];
  organization: Organization;
  workingDays: string[];
  nonWorkingDays: NoneWorkingDay[];
  workingTimes: WorkTime[];
  workingDay: string;
  workingTime: WorkTime;
  services: Service[];
  service: Service;
  status: Status;
  confirmationData?: ConfirmationData;
  loading: "idle" | "pending" | "succeeded" | "failed";
}

interface RootState {
  appointment: AppointmentState;
}

// Action types for better type handling

type OrganizationAction = {
  type: "appointment/setOrganization";
  payload: Organization;
};
type WorkingDaysAction = {
  type: "appointment/setWorkingDays";
  payload: { workingDays: string[] };
};
type WorkingTimesAction = {
  type: "appointment/setWorkingTimes";
  payload: WorkTime[];
};
type ClearAllDataAction = {
  type: "appointment/clearAlData";
};
type ServicesAction = {
  type: "appointment/setServices";
  payload: Service[];
};
type ServicesSaveAction = {
  type: "appointment/setService";
  payload: Service;
};

type DateAndTimeAction = {
  type: "appointment/setDateAndTime";
  payload: { workingDay: string; workingTime: WorkTime };
};
type StatusAction = {
  type: "appointment/setStatus";
  payload: Status;
};
type StatusCancelAction = {
  type: "appointment/setCancelStatus";
  payload: Status;
};
type ClearServiceAndDataAction = {
  type: "appointment/clearServiceAndDate";
};
type ClearDataAction = {
  type: "appointment/clearDate";
};
type ConfirmationDataAction = {
  type: "appointment/setConfirmationData";
  payload: ConfirmationData;
};
type ClearStatusAction = {
  type: "appointment/clearStatus";
};
const initialState: AppointmentState = {
  organizations: [],
  organization: {},
  workingDays: [],
  nonWorkingDays: [],
  workingTimes: [],
  workingDay: "",
  workingTime: {},
  services: [],
  service: {},
  status: {},
  loading: "idle",
};

export const appointmentReducer = createSlice({
  initialState,
  name: "appointment",
  reducers: {
    setOrganizations: (
      state: AppointmentState,
      action: PayloadAction<Organization[]>,
    ) => {
      return {
        ...state,
        organizations: action.payload,
      };
    },
    setOrganization: (
      state: AppointmentState,
      action: PayloadAction<Organization>,
    ) => {
      return {
        ...state,
        organization: action.payload,
      };
    },
    setWorkingDays: (
      state: AppointmentState,
      action: PayloadAction<{
        workingDays: string[];
      }>,
    ) => {
      return {
        ...state,
        workingDays: action.payload?.workingDays,
      };
    },
    setWorkingTimes: (
      state: AppointmentState,
      action: PayloadAction<WorkTime[]>,
    ) => {
      return {
        ...state,
        workingTimes: action.payload,
      };
    },
    setDateAndTime: (
      state: AppointmentState,
      action: PayloadAction<{ workingDay: string; workingTime: WorkTime }>,
    ) => {
      return {
        ...state,
        workingDay: action.payload?.workingDay,
        workingTime: action.payload?.workingTime,
      };
    },
    setServices: (
      state: AppointmentState,
      action: PayloadAction<Service[]>,
    ) => {
      return {
        ...state,
        services: action.payload,
      };
    },
    setService: (state: AppointmentState, action: PayloadAction<Service>) => {
      return {
        ...state,
        service: action.payload,
      };
    },
    createReservation: (state) => {
      Object.assign(state, initialState);
    },
    setStatus: (state: AppointmentState, action: PayloadAction<Status>) => {
      return {
        ...state,
        status: action.payload,
      };
    },
    setCancelStatus: (
      state: AppointmentState,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<Status>,
    ) => {
      return {
        ...state,
        status: {
          ...state?.status,
          status: {
            name: "Cancelled",
            value: 3,
          },
        },
      };
    },
    clearAlData: (state) => {
      Object.assign(state, initialState);
    },
    clearServiceAndDate: (state) => {
      return {
        ...state,
        workingDays: [],
        nonWorkingDays: [],
        workingTimes: [],
        workingDay: "",
        workingTime: {},
        services: [],
        service: {},
        status: {},
        confirmationData: undefined,
        loading: "idle",
      };
    },
    clearDate: (state) => {
      return {
        ...state,
        workingDays: [],
        nonWorkingDays: [],
        workingTimes: [],
        workingDay: "",
        workingTime: {},
        status: {},
        loading: "idle",
      };
    },
    setConfirmationData: (
      state: AppointmentState,
      action: PayloadAction<ConfirmationData>,
    ) => {
      return {
        ...state,
        confirmationData: action.payload,
      };
    },
    clearStatus: (state) => {
      return {
        ...state,
        status: {},
        loading: "idle",
      };
    },
  },
});

export const getOrganizationFunc = ({
  setLoading,
  id,
}: {
  setLoading: (loading: boolean) => void;
  id?: string;
}) => {
  return async (dispatch: Dispatch<OrganizationAction>) => {
    try {
      setLoading(true);
      const response = await getFunc(`Organizations/${id}`);

      if (response?.status === 200) {
        dispatch(setOrganization(response.data));
      }
      setLoading(false);
    } catch {
      toastFunc("Error fetching Organizations", "error");
      setLoading(false);
    }
  };
};

export const setOrganizationFunc = ({ data }: { data: Organization }) => {
  return async (dispatch: Dispatch<OrganizationAction>) => {
    try {
      dispatch(setOrganization(data));
    } catch {
      toastFunc("Error fetching users", "error");
    }
  };
};

export const getWorkingdaysFunc = ({
  setLoading,
  organizationId,
  serviceId,
  numberOfPeople,
  year,
  month,
}: {
  setLoading: (loading: boolean) => void;
  organizationId: string;
  serviceId?: string;
  numberOfPeople?: number;
  year?: number;
  month?: number;
}) => {
  return async (dispatch: Dispatch<WorkingDaysAction>) => {
    try {
      setLoading(true);
      // Perform parallel API calls
      const response = await getFunc(
        `InvalidDates?organizationId=${organizationId}&serviceId=${serviceId}&numberOfPeople=${numberOfPeople}&year=${year}&month=${month}`,
      );

      // Handle responses
      if (response?.status === 200) {
        const workingDays = response.data;

        // Dispatch the working days data to Redux
        dispatch(
          setWorkingDays({
            workingDays,
          }),
        );
      } else {
        throw new Error("Failed to fetch one of the resources");
      }
    } catch {
      toastFunc("Error fetching data", "error");
    } finally {
      setLoading(false);
    }
  };
};
export const getWorkingTimeFunc = ({
  setLoadingTime,
  id,
  serviceId,
  date,
  numberOfPeople,
}: {
  setLoadingTime: (loading: boolean) => void;
  id?: string;
  date?: string;
  serviceId?: string;
  numberOfPeople?: number;
}) => {
  return async (dispatch: Dispatch<WorkingTimesAction>) => {
    try {
      setLoadingTime(true);
      const response = await getFunc(
        `Timeslots?date=${date}&organizationId=${id}&serviceId=${serviceId}&numberOfPeople=${numberOfPeople}`,
      );
      if (response?.status === 200) {
        dispatch(setWorkingTimes(response.data?.value));
      }
      setLoadingTime(false);
    } catch {
      toastFunc("Error fetching Working days", "error");
      setLoadingTime(false);
    }
  };
};
export const setDateAndTimeFunc = ({
  date,
  time,
}: {
  date: string;
  time: WorkTime;
}) => {
  return async (dispatch: Dispatch<DateAndTimeAction>) => {
    try {
      dispatch(setDateAndTime({ workingDay: date, workingTime: time }));
    } catch {
      toastFunc("Error DateAndTime", "error");
    }
  };
};

export const getAllServicesFunc = ({
  setLoading,
  id,
}: {
  setLoading: (loading: boolean) => void;
  id: string;
}) => {
  return async (dispatch: Dispatch<ServicesAction>) => {
    try {
      setLoading(true);
      const response = await getFunc(`Services?organizationId=${id}`);
      if (response?.status === 200) {
        dispatch(setServices(response.data?.value));
      }

      setLoading(false);
    } catch {
      toastFunc("Error fetching Services", "error");
      setLoading(false);
    }
  };
};
export const setServiceFunc = ({ data }: { data: Service }) => {
  return async (dispatch: Dispatch<ServicesSaveAction>) => {
    try {
      dispatch(setService(data));
    } catch {
      toastFunc("Error Service", "error");
    }
  };
};

export interface ReservationCreateResult {
  code: string;
  reservationNumber: string;
}

export const createReservationFunc = ({
  setLoading,
  data,
  setValidation,
  setOpen,
  setCode,
}: {
  setLoading: (loading: boolean) => void;
  data: Confirmation;
  setValidation: (text: Validation) => void;
  setOpen: (value: boolean) => void;
  setCode: (value: ReservationCreateResult) => void;
}) => {
  return async () => {
    try {
      setLoading(true);
      const response = await postFunc("Reservations", data);

      if (response?.status === 200) {
        setCode(response?.data);
        setOpen(true);
        toastFunc("You have successfully booked an appointment", "success");
      }
      if (response?.data?.message == "CreatedMaxNumberOfReservationsForEmail") {
        toastFunc("CreatedMaxNumberOfReservationsForEmail", "error");
      } else if (response?.status === 400) {
        setValidation(response.data.errors || "");
      }

      setLoading(false);
    } catch {
      toastFunc("Error adding appointment", "error");
      setLoading(false);
    }
  };
};

export const statusFunc = ({
  setLoading,
  data,
  setValidation,
}: {
  setLoading: (loading: boolean) => void;
  setValidation: (text: string) => void;
  data: { code: string; email: string };
}) => {
  return async (dispatch: Dispatch<StatusAction>) => {
    try {
      setLoading(true);
      const response = await postFunc(
        "Reservations/CheckReservationStatus",
        data,
      );

      if (response?.status === 200) {
        dispatch(setStatus(response?.data));
      } else {
        setValidation("incorectData");
        toastFunc("Mail or Code is incorrect.", "error");
      }

      setLoading(false);
    } catch {
      toastFunc("Mail or Code is incorrect.", "error");
      setLoading(false);
    }
  };
};

export const clearAlDataFunc = () => {
  return async (dispatch: Dispatch<ClearAllDataAction>) => {
    try {
      dispatch(clearAlData());
    } catch {
      toastFunc("Error Clear data error", "error");
    }
  };
};

export const clearServiceAndDateFunc = () => {
  return async (dispatch: Dispatch<ClearServiceAndDataAction>) => {
    try {
      dispatch(clearServiceAndDate());
    } catch {
      toastFunc("Error Clear data error", "error");
    }
  };
};

export const clearDateFunc = () => {
  return async (dispatch: Dispatch<ClearDataAction>) => {
    try {
      dispatch(clearDate());
    } catch {
      toastFunc("Error Clear data error", "error");
    }
  };
};
export const cancelAppointmentFunc = ({
  setLoading,
  code,
  email,
  setValidation,
}: {
  setLoading: (loading: boolean) => void;
  setValidation?: (text: string) => void;
  code: string;
  email: string;
}) => {
  return async (dispatch: Dispatch<StatusCancelAction>) => {
    try {
      setLoading(true);
      const response = await postFunc("Reservations/CancelReservation", {
        code,
        email,
      });

      if (response?.status === 200) {
        dispatch(setCancelStatus(response?.data?.value));
      }

      if (response?.status === 400 && setValidation) {
        setValidation(response.data.errors || "");
      }
      setLoading(false);
    } catch {
      toastFunc("Error cancel Reservations", "error");
      setLoading(false);
    }
  };
};

export const setConfirmationDataFunc = ({
  data,
}: {
  data: ConfirmationData;
}) => {
  return async (dispatch: Dispatch<ConfirmationDataAction>) => {
    try {
      dispatch(setConfirmationData(data));
    } catch {
      toastFunc("Error Service", "error");
    }
  };
};
export const clearStatusFunc = () => {
  return async (dispatch: Dispatch<ClearStatusAction>) => {
    try {
      dispatch(clearStatus());
    } catch {
      toastFunc("Error Clear data error", "error");
    }
  };
};

export const {
  setOrganizations,
  setOrganization,
  setWorkingDays,
  setWorkingTimes,
  setDateAndTime,
  setServices,
  setService,
  createReservation,
  setStatus,
  clearAlData,
  setCancelStatus,
  clearServiceAndDate,
  clearDate,
  setConfirmationData,
  clearStatus,
} = appointmentReducer.actions;

export const usersData = (state: RootState) => state.appointment;

export default appointmentReducer.reducer;
